import { Link, useLocation } from "react-router-dom";
import { PERMISSION_LABELS, isPermissionKey } from "@delta/shared";
import { useLang } from "../i18n";
import { useSession } from "../lib/session";
import { Icon } from "../components/Icon";
import { findNav, PageHead, Shell } from "../components/Shell";

/** An audited screen that keeps its place in the menu until its phase (same pattern as the prototype's placeholder). */
export function NotBuilt() {
  const { t } = useLang();
  const { pathname } = useLocation();
  const hit = findNav(pathname);
  const title = hit ? t(hit.sub ?? hit.top.k) : pathname;
  const trail: [string, string?][] = hit?.sub ? [[t("home"), "/home"], [t(hit.top.k)], [title]] : [[t("home"), "/home"], [title]];
  return (
    <Shell navKey={pathname}>
      <PageHead crumbs={trail} title={title} />
      <div className="card placeholder" data-testid="not-built">
        <span className="kpi-ico"><Icon name={hit ? hit.top.ic : "file"} /></span>
        <h2>{t("notBuiltTitle")}</h2><p>{t("notBuiltBody")}</p>
        <p>{t("auditedPath")}: <code>{pathname}</code></p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}><Link className="btn" to="/home">{t("backHome")}</Link></div>
      </div>
    </Shell>
  );
}

/** An audited HR address: HR is handled by the separate HR system, so Delta has no screen for it. */
export function NotInDelta() {
  const { t } = useLang();
  const { pathname } = useLocation();
  return (
    <Shell navKey="">
      <PageHead crumbs={[[t("home"), "/home"], [t("notInDeltaTitle")]]} title={t("notInDeltaTitle")} />
      <div className="card placeholder" data-testid="not-in-delta">
        <span className="kpi-ico"><Icon name="users" /></span>
        <h2>{t("notInDeltaTitle")}</h2><p>{t("notInDeltaBody")}</p>
        <p>{t("auditedPath")}: <code>{pathname}</code></p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}><Link className="btn" to="/home">{t("backHome")}</Link></div>
      </div>
    </Shell>
  );
}

/** Shown when the address bar points at a screen the user lacks keys for. The API refuses the same requests. */
export function AccessDenied({ missing }: { missing: string[] }) {
  const { t, lang } = useLang();
  const { me } = useSession();
  const { pathname } = useLocation();
  const label = (k: string) => (k === "r*" ? t("anyReport") : isPermissionKey(k) ? PERMISSION_LABELS[k][lang] : k);
  // The package comes first (migration 043): no permission unlocks a module the business's package leaves out.
  const feature = missing.find((k) => k.startsWith("feature:"))?.slice(8);
  if (feature) return (
    <Shell navKey="">
      <PageHead crumbs={[[t("home"), "/home"], [t("pkgNotInPackage")]]} title={t("pkgNotInPackage")} />
      <section className="card">
        <div className="state-box denied" data-denied={pathname} data-feature={feature} data-testid="not-in-package">
          <Icon name="lock" />
          <h3>{t("pkgNotInPackageTitle", { f: t(`pkgFeature_${feature}`) })}</h3>
          <p>{t("pkgNotInPackageBody")}</p>
          <p className="hint">{t(me?.user.owner ? "pkgNotInPackageOwner" : "pkgNotInPackageStaff")}</p>
          <div className="denied-acts">
            {me?.user.owner && <Link className="btn btn-primary" to="/settings/package"><Icon name="box" />{t("setPackage")}</Link>}
            <Link className="btn" to="/home"><Icon name="home" />{t("backHome")}</Link>
          </div>
        </div>
      </section>
    </Shell>
  );
  return (
    <Shell navKey="">
      <PageHead crumbs={[[t("home"), "/home"], [t("accessDenied")]]} title={t("accessDenied")} badge={<span className="pill bad"><Icon name="lock" /> {me?.user.name}</span>} />
      <section className="card">
        <div className="state-box denied" data-denied={pathname}>
          <Icon name="lock" />
          <h3>{t("accessDeniedTitle")}</h3>
          <p>{t("accessDeniedBody", { u: me?.user.name ?? "" })}</p>
          <ul className="denied-keys">{missing.map((k) => <li key={k}><b>{label(k)}</b> <code dir="ltr">{k}</code></li>)}</ul>
          <p className="hint">{t("accessDeniedHint")}</p>
          <div className="denied-acts"><Link className="btn btn-primary" to="/home"><Icon name="home" />{t("backHome")}</Link></div>
        </div>
      </section>
    </Shell>
  );
}
